<!--
  detailsModalHost: orchestrates which page component is shown inside detailsModal.
  Listens for openDetailsModal / openNestedDetailsModal events, manages the view stack
  (Back navigation), and mounts the matching child (manageKPI, edit-control, objectives, etc.) with modalMode.
-->
<template>
	<detailsModal
		v-model="showDetailsModal"
		:title="detailsHeader"
		:showBack="detailsStack.length > 1"
		@close="closeDetailsModal"
		@back="backDetailsModal"
	>
	<manageKPI
		v-if="detailsView === 'manageKPI'"
		:key="'manageKPI-' + detailsParams.controlId"
		:controlId="detailsParams.controlId"
		:type="detailsParams.type"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<viewCompliance
		v-else-if="detailsView === 'viewCompliance'"
		:key="'viewCompliance-' + detailsParams.complianceId"
		:complianceId="detailsParams.complianceId"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<editCompliance
		v-else-if="detailsView === 'editCompliance'"
		:key="'editCompliance-' + detailsParams.complianceId"
		:complianceId="detailsParams.complianceId"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<complianceControls
		v-else-if="detailsView === 'complianceControls'"
		:key="'complianceControls-' + detailsParams.complianceId"
		:complianceId="detailsParams.complianceId"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<startCompliance
		v-else-if="detailsView === 'startCompliance'"
		:key="'startCompliance-' + detailsParams.complianceId"
		:complianceId="detailsParams.complianceId"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<taskEdit
		v-else-if="detailsView === 'complianceTask'"
		:key="'complianceTask-' + detailsParams.taskId"
		:complianceId="detailsParams.complianceId"
		:taskId="detailsParams.taskId"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<crTaskEdit
		v-else-if="detailsView === 'crTask'"
		:key="'crTask-' + detailsParams.taskId"
		:taskId="detailsParams.taskId"
		:complianceId="detailsParams.complianceId"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<findings
		v-else-if="detailsView === 'findings'"
		:key="'findings-' + detailsParams.findingId"
		:findingId="detailsParams.findingId"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<actionTaskEdit
		v-else-if="detailsView === 'actionTask'"
		:key="'actionTask-' + detailsParams.taskId"
		:findingId="detailsParams.findingId"
		:taskId="detailsParams.taskId"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<editControl
		v-else-if="detailsView === 'departmentControls'"
		:key="'departmentControls-' + detailsParams.id"
		:id="detailsParams.id"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<systemControlMapping
		v-else-if="detailsView === 'systemControlMapping'"
		:key="'systemControlMapping-' + detailsParams.systemId"
		:systemId="detailsParams.systemId"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<projectControlMapping
		v-else-if="detailsView === 'projectControls'"
		:key="'projectControls-' + detailsParams.id"
		:id="detailsParams.id"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<manageRootControlMap
		v-else-if="detailsView === 'manageRootControlMap'"
		:key="'manageRootControlMap-' + detailsParams.controlId"
		:controlId="detailsParams.controlId"
		:from="detailsParams.from"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	<objectiveControlsMap
		v-else-if="detailsView === 'objectiveControls'"
		:key="'objectiveControls-' + detailsParams.objectiveId"
		:objectiveId="detailsParams.objectiveId"
		:modalMode="true"
		@header-info="setDetailsHeader"
		@open-nested="openNested"
	/>
	</detailsModal>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { emitter } from '@/main';
import detailsModal from '@/components/detailsModal.vue';
import manageKPI from '@/pages/org-pages/manageKPI.vue';
import viewCompliance from '@/pages/org-pages/viewCompliance.vue';
import editCompliance from '@/pages/org-pages/compliance.vue';
import complianceControls from '@/pages/org-pages/complianceControls.vue';
import startCompliance from '@/pages/org-pages/startCompliance.vue';
import taskEdit from '@/pages/org-pages/taskEdit.vue';
import crTaskEdit from '@/pages/org-pages/crTaskEdit.vue';
import findings from '@/pages/org-pages/findings.vue';
import actionTaskEdit from '@/pages/org-pages/actionTaskEdit.vue';
import editControl from '@/pages/org-pages/edit-control.vue';
import systemControlMapping from '@/pages/org-pages/systemControlMapping.vue';
import projectControlMapping from '@/pages/org-pages/projectControlMapping.vue';
import manageRootControlMap from '@/pages/root/manageRootControlMap.vue';
import objectiveControlsMap from '@/pages/org-pages/objectiveControlsMap.vue';

export default defineComponent({
	name: 'detailsModalHost',
	components: {
		detailsModal,
		manageKPI,
		viewCompliance,
		editCompliance,
		complianceControls,
		startCompliance,
		taskEdit,
		crTaskEdit,
		findings,
		actionTaskEdit,
		editControl,
		systemControlMapping,
		projectControlMapping,
		manageRootControlMap,
		objectiveControlsMap,
	},
	data(): any {
		return {
			showDetailsModal: false,
			detailsView: '',
			detailsParams: {},
			detailsHeader: '',
			detailsStack: [] as any[],
		};
	},
	methods: {
		openDetailsModal(payload: any) {
			const entry = {
				view: payload.view,
				params: payload.params || {},
				title: payload.title || '',
			};
			if (this.showDetailsModal) {
				this.detailsStack.push(entry);
			} else {
				this.detailsStack = [entry];
				this.showDetailsModal = true;
				document.body.style.overflow = 'hidden';
			}
			this.applyEntry(entry);
		},
		openNested(payload: any) {
			const entry = {
				view: payload.view,
				params: payload.params || {},
				title: payload.title || this.detailsHeader,
			};
			this.detailsStack.push(entry);
			this.applyEntry(entry);
		},
		applyEntry(entry: any) {
			this.detailsView = entry.view;
			this.detailsParams = { ...entry.params };
			this.detailsHeader = entry.title || '';
		},
		setDetailsHeader(info: any) {
			if (typeof info === 'string') {
				this.detailsHeader = info;
				return;
			}
			this.detailsHeader = info?.title || info?.header || this.detailsHeader;
		},
		backDetailsModal() {
			if (this.detailsStack.length < 2) {
				this.closeDetailsModal();
				return;
			}
			this.detailsStack.pop();
			this.applyEntry(this.detailsStack[this.detailsStack.length - 1]);
		},
		closeDetailsModal() {
			const closedView = this.detailsView;
			const closedParams = { ...this.detailsParams };
			this.showDetailsModal = false;
			this.detailsView = '';
			this.detailsParams = {};
			this.detailsHeader = '';
			this.detailsStack = [];
			document.body.style.overflow = '';
			// Defer so modal children unmount before list pages rebind row-click listeners
			this.$nextTick(() => {
				emitter.emit('detailsModalClosed', { view: closedView, params: closedParams });
			});
		},
	},
	mounted() {
		emitter.on('openDetailsModal', this.openDetailsModal);
		emitter.on('openNestedDetailsModal', this.openNested);
	},
	beforeUnmount() {
		emitter.off('openDetailsModal', this.openDetailsModal);
		emitter.off('openNestedDetailsModal', this.openNested);
		this.closeDetailsModal();
	},
});
</script>
